// Écran 4 — Corrections du DSI : proposer une correction, voir la simulation (différence exacte), valider ; gérer
// le conflit 412 ; retirer avec un motif. Il n'y a pas de bouton « supprimer » : l'API n'en a pas, et c'est voulu.
import { useMemo, useState, type FormEvent } from 'react';
import type { Lien, Objet, ObjetConstat } from '../api/types';
import { Attendre, BlocProvenance, Erreur, Etiquette } from '../composants/communs';
import { EtapesEcriture, PanneauEcriture, useEcriture } from '../composants/Ecriture';
import { useAuteur, useCarte, useConstats, useEkera, useObjetsRetires } from '../contexte';
import { indexer, nomDe, type IndexCarte } from '../domaine/carte';
import {
  correctionCreerTravaux,
  correctionModifier,
  correctionRetirer,
  correctionValider,
  libelleElement,
  lireSaisie,
  natureDe,
  saisieDe,
  type NatureValeur,
} from '../domaine/corrections';
import { dateFr, LIBELLE_TYPE } from '../domaine/format';
import { naviguer } from '../routeur';
import { useQuery, useQueryClient } from '@tanstack/react-query';

export function EcranCorrections({ params }: { params: URLSearchParams }) {
  const carte = useCarte();
  const constats = useConstats();
  const { ecritureAutorisee, motifLectureSeule } = useEkera();
  const mode = params.get('mode') === 'travaux' ? 'travaux' : 'corriger';
  return (
    <div className="pile">
      <p className="sous-titre">
        Ekera propose, vous validez. Chaque écriture est d’abord simulée (la différence exacte s’affiche), puis écrite avec
        la version que vous avez lue : si l’élément a changé entre-temps, rien n’est écrasé. Rien ne se supprime : un
        élément se retire, avec un motif, et reste lisible.
      </p>
      {!ecritureAutorisee && (
        <p className="encadre attention" role="status" data-testid="lecture-seule">
          {motifLectureSeule} Les corrections, validations, retraits et travaux ne sont pas proposés.
        </p>
      )}
      {ecritureAutorisee && (
      <>
      <div className="onglets" role="group" aria-label="Type de proposition">
        <button type="button" aria-pressed={mode === 'corriger'} onClick={() => naviguer('corrections')}>
          Corriger, valider ou retirer
        </button>
        <button type="button" aria-pressed={mode === 'travaux'} onClick={() => naviguer('corrections', { mode: 'travaux' })}>
          Proposer des travaux
        </button>
      </div>
      <Attendre etats={[carte, constats]} quoi="la carte">
        {() =>
          mode === 'travaux' ? (
            <FormTravaux constats={constats.data!.constats} constatInitial={params.get('constat')} />
          ) : (
            <Corriger ix={indexer(carte.data!)} elementInitial={params.get('element')} />
          )
        }
      </Attendre>
      </>
      )}
      <Retires />
    </div>
  );
}

type Op = 'modifier' | 'valider' | 'retirer';

function Corriger({ ix, elementInitial }: { ix: IndexCarte; elementInitial: string | null }) {
  const { client, cid } = useEkera();
  const auteur = useAuteur();
  const id = elementInitial ?? '';
  const estLien = ix.liens.some((l) => l.id === id);
  // Lecture fraîche de l'élément avec son ETag : c'est cette version qui part dans If-Match.
  const lecture = useQuery({
    queryKey: ['carte', cid, 'element', id],
    queryFn: () => client.element(cid, id, estLien ? 'lien' : 'objet'),
    enabled: !!id,
    staleTime: 0,
  });
  const qc = useQueryClient();
  const lu = lecture.data ?? null;
  // Conflit : la version actuelle, relue, devient la version lue ; la saisie du DSI est gardée.
  const reprendre = (actuel: { element: Objet | Lien; etag: string }) =>
    qc.setQueryData(['carte', cid, 'element', id], actuel);

  const groupes = useMemo(() => {
    const parType = new Map<string, Objet[]>();
    for (const o of ix.objets) parType.set(o.type, [...(parType.get(o.type) ?? []), o]);
    return [...parType.entries()].sort((a, b) => a[0].localeCompare(b[0]));
  }, [ix]);
  const passages = ix.liens.filter((l) => l.type === 'passage_reseau');

  return (
    <div className="pile">
      <section className="panneau" aria-labelledby="t-choix">
        <h2 id="t-choix">Élément à corriger</h2>
        <div className="champ">
          <label htmlFor="c-element">Objet ou lien</label>
          <select id="c-element" value={id} onChange={(e) => naviguer('corrections', { element: e.target.value || undefined })}>
            <option value="">Choisissez…</option>
            {groupes.map(([t, objets]) => (
              <optgroup key={t} label={LIBELLE_TYPE[t as Objet['type']]}>
                {objets.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.nom}
                  </option>
                ))}
              </optgroup>
            ))}
            <optgroup label="Passages réseau">
              {passages.map((l) => (
                <option key={l.id} value={l.id}>
                  {libelleElement(l, (x) => nomDe(ix, x))}
                </option>
              ))}
            </optgroup>
          </select>
        </div>
        {lecture.error ? <Erreur erreur={lecture.error} titre="Lecture de l’élément impossible." /> : null}
      </section>
      {lu && (
        <Formulaire
          key={lu.element.id}
          lu={lu}
          auteur={auteur}
          nom={(x) => nomDe(ix, x)}
          surReprendre={reprendre}
        />
      )}
    </div>
  );
}

interface Champ {
  cle: string;
  nature: NatureValeur;
  saisie: string;
  nouveau: boolean;
}

function Formulaire({
  lu,
  auteur,
  nom,
  surReprendre,
}: {
  lu: { element: Objet | Lien; etag: string };
  auteur: string;
  nom: (id: string) => string;
  surReprendre: (actuel: { element: Objet | Lien; etag: string }) => void;
}) {
  const el = lu.element;
  const attributs = (el.attributs ?? {}) as Record<string, unknown>;
  const [op, setOp] = useState<Op>('modifier');
  const [champs, setChamps] = useState<Champ[]>(() =>
    Object.entries(attributs)
      .filter(([, v]) => natureDe(v) !== 'autre')
      .map(([k, v]) => ({ cle: k, nature: natureDe(v), saisie: saisieDe(v), nouveau: false })),
  );
  const [nomSaisi, setNomSaisi] = useState('nom' in el ? el.nom : '');
  const [motif, setMotif] = useState('');
  const [erreurSaisie, setErreurSaisie] = useState<string | null>(null);
  const ecr = useEcriture(lu.etag, el);

  function construire() {
    if (op === 'retirer') return correctionRetirer(el, { motif, auteur });
    if (op === 'valider') return correctionValider(el, { motif, auteur });
    const nouveaux: Record<string, unknown> = {};
    for (const c of champs) {
      if (!c.cle.trim()) continue;
      nouveaux[c.cle.trim()] = lireSaisie(c.nature, c.saisie, attributs[c.cle]);
    }
    const changes: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(nouveaux)) if (JSON.stringify(attributs[k]) !== JSON.stringify(v)) changes[k] = v;
    const nomChange = 'nom' in el && nomSaisi !== el.nom;
    if (!Object.keys(changes).length && !nomChange) throw new Error('Aucun changement : modifiez au moins une valeur.');
    return correctionModifier(el, changes, { nom: nomChange ? nomSaisi : undefined, motif, auteur });
  }

  function proposer(e: FormEvent) {
    e.preventDefault();
    setErreurSaisie(null);
    try {
      void ecr.simuler(construire());
    } catch (err) {
      setErreurSaisie(err instanceof Error ? err.message : String(err));
    }
  }

  const verrouille = ecr.etat.etape !== 'saisie';
  return (
    <>
      <section className="panneau" aria-labelledby="t-form">
        <div className="panneau-tete">
          <h2 id="t-form">{libelleElement(el, nom)}</h2>
          <span className="ligne">
            {el.retire && <Etiquette ton="neutre">Retiré</Etiquette>}
            <span className="indice">
              Version lue : <span className="num">{lu.etag}</span>
            </span>
          </span>
        </div>
        <EtapesEcriture etat={ecr.etat} />
        <form className="pile" onSubmit={proposer} aria-describedby="aide-form">
          <fieldset disabled={verrouille}>
            <legend>Ce que vous voulez faire</legend>
            <div className="ligne">
              {(
                [
                  ['modifier', 'Corriger des valeurs'],
                  ['valider', 'Valider tel quel'],
                  ['retirer', 'Retirer de la carte'],
                ] as [Op, string][]
              ).map(([v, l]) => (
                <label key={v} className="case">
                  <input type="radio" name="op" value={v} checked={op === v} onChange={() => setOp(v)} />
                  {l}
                </label>
              ))}
            </div>
          </fieldset>
          <p id="aide-form" className="note">
            {op === 'modifier' && '« inconnu » enregistre une valeur à demander en entretien ; un champ vide pour oui/non aussi.'}
            {op === 'valider' && 'La valeur proposée par Ekera devient « confirmée », à votre nom.'}
            {op === 'retirer' &&
              'L’élément sort de la carte et des calculs, mais reste lisible avec votre motif. Rien n’est supprimé ; il peut être recréé.'}
          </p>
          {op === 'modifier' && (
            <fieldset disabled={verrouille} className="pile" style={{ gap: 10 }}>
              <legend>Valeurs</legend>
              {'nom' in el && (
                <div className="champ">
                  <label htmlFor="c-nom">Nom</label>
                  <input id="c-nom" type="text" value={nomSaisi} onChange={(e) => setNomSaisi(e.target.value)} />
                </div>
              )}
              {champs.map((c, i) => (
                <div className="grille g2" key={`${c.cle}-${i}`} style={{ alignItems: 'end' }}>
                  {c.nouveau ? (
                    <div className="champ">
                      <label htmlFor={`c-cle-${i}`}>Nouvel attribut</label>
                      <input
                        id={`c-cle-${i}`}
                        type="text"
                        value={c.cle}
                        onChange={(e) => setChamps(champs.map((x, j) => (j === i ? { ...x, cle: e.target.value } : x)))}
                      />
                    </div>
                  ) : null}
                  <ChampValeur
                    id={`c-val-${i}`}
                    champ={c}
                    surSaisie={(s) => setChamps(champs.map((x, j) => (j === i ? { ...x, saisie: s } : x)))}
                  />
                </div>
              ))}
              <div>
                <button
                  type="button"
                  className="btn petit"
                  onClick={() => setChamps([...champs, { cle: '', nature: 'texte', saisie: '', nouveau: true }])}
                >
                  Ajouter un attribut
                </button>
              </div>
            </fieldset>
          )}
          <div className="champ">
            <label htmlFor="c-motif">
              Motif {op === 'retirer' ? '(obligatoire)' : '(facultatif)'}
              <span className="aide"> : il est gardé dans la provenance et le journal.</span>
            </label>
            <textarea
              id="c-motif"
              value={motif}
              required={op === 'retirer'}
              disabled={verrouille}
              onChange={(e) => setMotif(e.target.value)}
              placeholder={op === 'retirer' ? 'Ex. : serveur décommissionné en septembre (ticket 4512)' : ''}
            />
          </div>
          {erreurSaisie && (
            <p className="encadre erreur" role="alert">
              {erreurSaisie}
            </p>
          )}
          {!verrouille && (
            <div className="ligne">
              <button type="submit" className="btn sombre" disabled={ecr.occupe || (op === 'retirer' && !motif.trim())}>
                {ecr.occupe ? 'Simulation…' : 'Voir la simulation'}
              </button>
            </div>
          )}
        </form>
      </section>
      <PanneauEcriture
        ecr={ecr}
        surReprendre={(actuel) => {
          surReprendre(actuel);
          ecr.reinitialiser();
        }}
        libelleValider={op === 'retirer' ? 'Retirer de la carte' : op === 'valider' ? 'Valider ce fait' : 'Écrire la correction'}
      />
      <details className="panneau">
        <summary>Provenance actuelle</summary>
        <BlocProvenance p={el.provenance} />
      </details>
    </>
  );
}

function ChampValeur({ id, champ, surSaisie }: { id: string; champ: Champ; surSaisie: (s: string) => void }) {
  const libelle = champ.nouveau ? 'Valeur' : champ.cle.replace(/_/g, ' ');
  if (champ.nature === 'booleen') {
    return (
      <div className="champ">
        <label htmlFor={id}>{libelle}</label>
        <select id={id} value={champ.saisie === 'inconnu' ? '' : champ.saisie} onChange={(e) => surSaisie(e.target.value)}>
          <option value="oui">oui</option>
          <option value="non">non</option>
          <option value="">inconnu</option>
        </select>
      </div>
    );
  }
  return (
    <div className="champ">
      <label htmlFor={id}>
        {libelle}
        {champ.nature === 'montant' && <span className="aide"> (euros)</span>}
      </label>
      <input
        id={id}
        type="text"
        inputMode={champ.nature === 'nombre' || champ.nature === 'montant' ? 'decimal' : undefined}
        value={champ.saisie}
        onChange={(e) => surSaisie(e.target.value)}
      />
    </div>
  );
}

function FormTravaux({ constats, constatInitial }: { constats: ObjetConstat[]; constatInitial: string | null }) {
  const auteur = useAuteur();
  const initial = constats.find((c) => c.id === constatInitial);
  const [nom, setNom] = useState(initial ? `Corriger : ${initial.nom}` : '');
  const [description, setDescription] = useState(initial?.attributs?.recommandation ?? initial?.attributs?.description ?? '');
  const [cout, setCout] = useState('');
  const [delai, setDelai] = useState('');
  const [constat, setConstat] = useState(initial?.id ?? '');
  const [motif, setMotif] = useState('');
  const [erreur, setErreur] = useState<string | null>(null);
  const ecr = useEcriture(undefined, null);
  const verrouille = ecr.etat.etape !== 'saisie';

  function proposer(e: FormEvent) {
    e.preventDefault();
    setErreur(null);
    try {
      if (!nom.trim()) throw new Error('Donnez un nom au chantier.');
      const n = (t: string) => (t.trim() ? (lireSaisie('nombre', t) as number) : null);
      void ecr.simuler(
        correctionCreerTravaux({ nom: nom.trim(), description, cout: n(cout), delaiJours: n(delai), constat: constat || undefined }, { auteur, motif }),
      );
    } catch (err) {
      setErreur(err instanceof Error ? err.message : String(err));
    }
  }
  return (
    <>
      <section className="panneau" aria-labelledby="t-trav">
        <h2 id="t-trav">Nouveau chantier</h2>
        <EtapesEcriture etat={ecr.etat} />
        <form className="pile" onSubmit={proposer}>
          <fieldset disabled={verrouille} className="pile" style={{ gap: 10 }}>
            <legend>Chantier proposé</legend>
            <div className="champ">
              <label htmlFor="t-constat">Constat traité</label>
              <select id="t-constat" value={constat} onChange={(e) => setConstat(e.target.value)}>
                <option value="">Aucun</option>
                {constats.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.nom}
                  </option>
                ))}
              </select>
            </div>
            <div className="champ">
              <label htmlFor="t-nom">Nom</label>
              <input id="t-nom" type="text" value={nom} onChange={(e) => setNom(e.target.value)} required />
            </div>
            <div className="champ">
              <label htmlFor="t-desc">Description</label>
              <textarea id="t-desc" value={description} onChange={(e) => setDescription(e.target.value)} />
            </div>
            <div className="grille g2">
              <div className="champ">
                <label htmlFor="t-cout">
                  Coût estimé <span className="aide">(euros, ordre de grandeur à confirmer par devis)</span>
                </label>
                <input id="t-cout" type="text" inputMode="decimal" value={cout} onChange={(e) => setCout(e.target.value)} />
              </div>
              <div className="champ">
                <label htmlFor="t-delai">Délai (jours)</label>
                <input id="t-delai" type="text" inputMode="numeric" value={delai} onChange={(e) => setDelai(e.target.value)} />
              </div>
            </div>
            <div className="champ">
              <label htmlFor="t-motif">Motif (facultatif)</label>
              <input id="t-motif" type="text" value={motif} onChange={(e) => setMotif(e.target.value)} />
            </div>
          </fieldset>
          {erreur && (
            <p className="encadre erreur" role="alert">
              {erreur}
            </p>
          )}
          {!verrouille && (
            <div>
              <button type="submit" className="btn sombre" disabled={ecr.occupe}>
                Voir la simulation
              </button>
            </div>
          )}
        </form>
      </section>
      <PanneauEcriture ecr={ecr} surReprendre={() => ecr.reinitialiser()} libelleValider="Créer le chantier" />
    </>
  );
}

function Retires() {
  const retires = useObjetsRetires();
  if (!retires.data?.length) return null;
  return (
    <section className="panneau" aria-labelledby="t-retires">
      <h2 id="t-retires">Éléments retirés ({retires.data.length})</h2>
      <p className="note">Ils restent lisibles et ne comptent plus dans les calculs.</p>
      <ul className="liste-points">
        {retires.data.map((o) => (
          <li key={o.id}>
            <b>{o.nom}</b>
            <small>
              Retiré par {o.retire?.par ?? '—'} le {dateFr(o.retire?.le)} : {o.retire?.motif}
            </small>
          </li>
        ))}
      </ul>
    </section>
  );
}
